// filepath: apps/web/src/features/admin/audit/AuditViewerTable.tsx
//
// FR-UI-ADM-14 — Renders each AuditEntry row.
//
// INV-9: all fields rendered as text nodes (no dangerouslySetInnerHTML).
// AG-1..6: agent_principal / task_spec_id / orchestrator_principal lines
//          with data-agent="true" attribute (color not sole carrier).
// AG-5: version transition v<N> → v<N+1> when both ends are set.

import { useTranslation } from "@/i18n/useTranslation";
import type { AuditEntry } from "./types";

export interface AuditViewerTableProps {
  entries: readonly AuditEntry[];
}

export function AuditViewerTable({ entries }: AuditViewerTableProps) {
  const { t, locale } = useTranslation();
  if (entries.length === 0) {
    return <p className="audit-viewer__empty">{t("audit.empty")}</p>;
  }
  return (
    <table className="audit-viewer__table">
      <thead>
        <tr>
          <th scope="col">{t("audit.column.definition_type")}</th>
          <th scope="col">{t("audit.column.definition_id")}</th>
          <th scope="col">{t("audit.column.action")}</th>
          <th scope="col">{t("audit.column.author")}</th>
          <th scope="col">{t("audit.column.version")}</th>
          <th scope="col">{t("audit.column.created_at")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{e.definition_type}</td>
            <td>{e.definition_id}</td>
            <td>{e.action}</td>
            <td>
              <div className="audit-row__author">{e.author_identity}</div>
              {e.agent_principal && (
                <div
                  className="audit-row__agent"
                  data-agent="true"
                  aria-label={t("audit.column.agent_label")}
                >
                  {t("audit.column.agent")}: {e.agent_principal}
                </div>
              )}
              {e.task_spec_id && (
                <div className="audit-row__task-spec">
                  {t("audit.column.task_spec")}: {e.task_spec_id}
                </div>
              )}
              {e.orchestrator_principal && (
                <div className="audit-row__orchestrator">
                  {t("audit.column.orchestrator")}: {e.orchestrator_principal}
                </div>
              )}
            </td>
            <td>
              {e.version_before !== null && e.version_after !== null
                ? `v${e.version_before} → v${e.version_after}`
                : "—"}
            </td>
            <td>
              <time dateTime={e.created_at}>
                {new Date(e.created_at).toLocaleString(locale)}
              </time>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
